/**
 * N9-v2 — central route → chunk preload map.
 *
 * Shared by `<PreloadLink>` (wraps react-router-dom Link) and
 * `<ClickableCard>` (non-anchor navigable card). Both prefetch the
 * target's JS chunk on hover/focus so the Suspense boundary in
 * routes-client.tsx doesn't block the click.
 *
 * Keep the patterns here in sync with routes-client.tsx. If a new
 * route is added there, add it here too. Missing entries fail silent
 * (no preload, no error) — worst case is the existing Suspense
 * fallback still shows for ~100 ms while the chunk loads.
 */

// Order matters — deeper route patterns must come before shallower
// ones so /a/b/c/d matches ProductDetail before ProductLine.
const PRELOAD_MAP: Array<[RegExp, () => Promise<unknown>]> = [
  [/^\/$/, () => import("@/pages/Index")],
  [/^\/sobre\/?$/, () => import("@/pages/About")],
  [/^\/eventos\/?$/, () => import("@/pages/Events")],
  [/^\/eventos\/[^/]+\/?$/, () => import("@/pages/EventDetail")],
  [/^\/campanhas\/[^/]+\/?$/, () => import("@/pages/Campaign")],
  [/^\/contato\/?$/, () => import("@/pages/Contact")],
  [/^\/servicos\/?$/, () => import("@/pages/Services")],
  [/^\/servicos\/[^/]+\/?$/, () => import("@/pages/ServicePage")],
  [/^\/produtos\/[^/]+\/[^/]+\/[^/]+\/[^/]+\/?$/, () => import("@/pages/ProductDetail")],
  [/^\/produtos\/[^/]+\/[^/]+\/[^/]+\/?$/, () => import("@/pages/ProductGroup")],
  [/^\/produtos\/[^/]+\/[^/]+\/?$/, () => import("@/pages/ProductFamily")],
  [/^\/produtos\/[^/]+\/?$/, () => import("@/pages/ProductLine")],
  [/^\/busca\/?/, () => import("@/pages/Search")],
  [/^\/comparador\/?/, () => import("@/pages/Compare")],
];

/**
 * Resolve a pathname to its preload loader (or `null` if no pattern
 * matches). Pure function — does NOT fire the loader. Exposed so
 * unit tests can assert that the regex map routes each path to the
 * correct destination without actually importing every page chunk.
 *
 * Iteration order = PRELOAD_MAP order, so the first match wins.
 * Patterns are arranged deepest-first; see the comment on
 * PRELOAD_MAP for why.
 */
export function resolvePreloader(
  pathname: string,
): (() => Promise<unknown>) | null {
  for (const [pattern, loader] of PRELOAD_MAP) {
    if (pattern.test(pathname)) return loader;
  }
  return null;
}

/**
 * Trigger the dynamic import for the given path's route chunk.
 * Dynamic imports are globally de-duplicated by the browser, so calling
 * this repeatedly is free — but callers should still guard with their
 * own once-flag to avoid the regex-loop work per mousemove.
 *
 * Fire-and-forget. Errors are swallowed; the real click-time render
 * will surface them via React's ErrorBoundary.
 */
export function preloadRouteChunk(pathname: string): void {
  const loader = resolvePreloader(pathname);
  if (loader) loader().catch(() => {});
}

/**
 * Inject `<link rel="preload" as="image">` for a hero image the user is
 * about to navigate to. The browser starts the download immediately at
 * high priority, so by the time the destination route mounts and emits
 * the <img>, the image is already in the HTTP cache.
 *
 * When responsive `sources` are provided, we emit `imagesrcset` +
 * `imagesizes` so the browser picks the SAME variant the destination
 * <img> will request via srcset. Without this, a single-URL preload
 * targets the wrong size variant and the browser issues a second
 * fetch for the size srcset actually picks — defeating the preload.
 *
 * Idempotent — repeated calls for the same href are a no-op.
 */
type PreloadImageSources = {
  thumbnail?: string;
  thumbnail_2x?: string;
  card?: string;
  card_2x?: string;
  hero?: string;
  hero_2x?: string;
};

const imagePreloadCache = new Set<string>();

export function preloadImage(
  href: string | undefined,
  sources?: PreloadImageSources,
  sizes?: string,
): void {
  if (!href) return;
  if (typeof document === "undefined") return;

  // Build a srcset string from the sources object, mirroring FadeImage's
  // ordering. Width descriptors match the CMS Media size config.
  const SIZE_W: Record<keyof PreloadImageSources, number> = {
    thumbnail: 300,
    thumbnail_2x: 600,
    card: 600,
    card_2x: 1200,
    hero: 1200,
    hero_2x: 2400,
  };
  const srcsetParts = sources
    ? (Object.entries(sources) as Array<[keyof PreloadImageSources, string | undefined]>)
        .filter((entry): entry is [keyof PreloadImageSources, string] => Boolean(entry[1]))
        .map(([key, url]) => `${url} ${SIZE_W[key]}w`)
    : [];
  const srcset = srcsetParts.length > 0 ? srcsetParts.join(", ") : undefined;

  // Dedupe key includes the srcset because the same href may surface in
  // different variant sets across calls (rare, but cheap to be safe).
  const key = srcset ? `${href}::${srcset}::${sizes ?? ""}` : href;
  if (imagePreloadCache.has(key)) return;
  imagePreloadCache.add(key);

  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = href;
  link.fetchPriority = "high";
  if (srcset) {
    // imagesrcset / imagesizes are the responsive equivalents of srcset
    // / sizes for <link rel=preload as=image>. Chrome 73+, FF 78+, Safari 16+.
    (link as HTMLLinkElement & { imageSrcset?: string; imageSizes?: string }).imageSrcset = srcset;
    if (sizes) {
      (link as HTMLLinkElement & { imageSrcset?: string; imageSizes?: string }).imageSizes = sizes;
    }
  }
  document.head.appendChild(link);
}

/**
 * Idle-time preload of the heavy menu-target chunks. Called once on
 * Header mount. Whichever page the user landed on, after the browser
 * goes idle the four routes that the mega-menu links to are already
 * in the chunk cache — so a click on Rebites (or any sibling family,
 * or Serviços) from any source page commits without a Suspense
 * fallback. Without this, content-page → menu-target navigation feels
 * laggy because the click→hover window is too short for a cold fetch.
 *
 * Routes that aren't menu-direct (Search, Compare, ProductDetail,
 * EventDetail) stay on hover-only preload — they're rarer
 * destinations and not worth the up-front bytes.
 *
 * Falls back to setTimeout when requestIdleCallback isn't available
 * (Safari < 16). Awaiting `idle` first keeps this off the LCP path.
 */
const MENU_TARGET_LOADERS: Array<() => Promise<unknown>> = [
  () => import("@/pages/ProductLine"),
  () => import("@/pages/ProductFamily"),
  () => import("@/pages/ProductGroup"),
  () => import("@/pages/Services"),
  () => import("@/pages/ServicePage"),
];

let idlePreloadFired = false;

export function preloadMenuTargetsAtIdle(): void {
  if (idlePreloadFired) return;
  idlePreloadFired = true;

  const fire = () => {
    for (const load of MENU_TARGET_LOADERS) load().catch(() => {});
  };

  if (typeof window === "undefined") return;
  // The `in` check narrows `window` to "has requestIdleCallback" in
  // the if branch, which TS then narrows the else branch to "doesn't
  // have it" — including stripping setTimeout in the negative narrow.
  // Use the standalone global instead of `window.setTimeout` to side-
  // step that narrowing.
  type IdleWin = Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void };
  if ("requestIdleCallback" in window) {
    (window as IdleWin).requestIdleCallback(fire, { timeout: 2000 });
  } else {
    // Safari < 16: defer past the LCP and any synchronous boot work.
    setTimeout(fire, 1500);
  }
}
